import { useState } from 'react';
import Modal from 'components/Modal';
import { useTranslation } from 'react-i18next';
import { KoobButton } from '@koob/ui';
import Layout from 'components/Hotel/Booking/Layout';
import { Stack } from 'react-bootstrap';
import BookingForm from '../../BookingForm';
import { getBooking } from 'api/bookings';
import { useAsync } from 'react-async';

const TripModal = ({ onRequestClose, title, booking }) => {
  const { t } = useTranslation('booking', { keyPrefix: 'trip' });

  return (
    <Modal
      size="large"
      isOpen
      title={title}
      onRequestClose={onRequestClose}
      footer={
        <Stack size="full" alignX="flex-end">
          <KoobButton variant="primary" onClick={onRequestClose}>
            {t('misc:close')}
          </KoobButton>
        </Stack>
      }
    >
      <Layout>{booking && <BookingForm booking={booking} />}</Layout>
    </Modal>
  );
};

const TripItem = ({ title, booking, kind = 'experience' }) => {
  const { t } = useTranslation('booking', { keyPrefix: 'trip' });

  const { data } = useAsync({
    promiseFn: getBooking,
    bookingId: booking?.id,
  });

  const [activeTripModalKind, setActiveTripModalKind] = useState();
  const bookingTypeIcon = {
    hotel: (
      <svg
        className="w-[24px] h-[24px]"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 640 512"
      >
        <path d="M48 336H288V184C288 153.1 313.1 128 344 128H520C586.3 128 640 181.7 640 248V464C640 477.3 629.3 488 616 488C602.7 488 592 477.3 592 464V384H48V456C48 469.3 37.25 480 24 480C10.75 480 0 469.3 0 456V56C0 42.75 10.75 32 24 32C37.25 32 48 42.75 48 56V336zM520 176H344C339.6 176 336 179.6 336 184V336H592V248C592 208.2 559.8 176 520 176zM256 216C256 264.6 216.6 304 168 304C119.4 304 80 264.6 80 216C80 167.4 119.4 128 168 128C216.6 128 256 167.4 256 216zM168 176C145.9 176 128 193.9 128 216C128 238.1 145.9 256 168 256C190.1 256 208 238.1 208 216C208 193.9 190.1 176 168 176z" />
      </svg>
    ),
    experience: (
      <svg className="h-5 w-5" viewBox="0 0 512 512">
        <path d="M234.2 4.672C241 1.592 248.5 0 256 0C263.5 0 270.1 1.592 277.8 4.672L495.2 102.1C505.4 106.7 512 116.8 512 128C512 139.2 505.4 149.3 495.2 153.9L277.8 251.3C270.1 254.4 263.5 256 256 256C248.5 256 241 254.4 234.2 251.3L16.76 153.9C6.561 149.3 .0003 139.2 .0003 128C.0003 116.8 6.561 106.7 16.76 102.1L234.2 4.672zM256 32C252.1 32 249.1 32.64 247.2 33.87L37.27 128L247.2 222.1C249.1 223.4 252.1 224 256 224C259 224 262 223.4 264.8 222.1L474.7 128L264.8 33.87C262 32.64 259 32 256 32V32zM78.6 219.9C82.22 228 78.61 237.5 70.55 241.1L37.27 256L247.2 350.1C249.1 351.4 252.1 352 256 352C259 352 262 351.4 264.8 350.1L474.7 256L441.5 241.1C433.4 237.5 429.8 228 433.4 219.9C437 211.9 446.5 208.3 454.5 211.9L495.2 230.1C505.4 234.7 512 244.8 512 256C512 267.2 505.4 277.3 495.2 281.9L277.8 379.3C270.1 382.4 263.5 384 256 384C248.5 384 241 382.4 234.2 379.3L16.76 281.9C6.561 277.3 0 267.2 0 256C0 244.8 6.561 234.7 16.76 230.1L57.46 211.9C65.52 208.3 74.99 211.9 78.6 219.9H78.6zM37.27 384L247.2 478.1C249.1 479.4 252.1 480 256 480C259 480 262 479.4 264.8 478.1L474.7 384L441.5 369.1C433.4 365.5 429.8 356 433.4 347.9C437 339.9 446.5 336.3 454.5 339.9L495.2 358.1C505.4 362.7 512 372.8 512 384C512 395.2 505.4 405.3 495.2 409.9L277.8 507.3C270.1 510.4 263.5 512 256 512C248.5 512 241 510.4 234.2 507.3L16.76 409.9C6.561 405.3 0 395.2 0 384C0 372.8 6.561 362.7 16.76 358.1L57.46 339.9C65.52 336.3 74.99 339.9 78.6 347.9C82.21 356 78.61 365.5 70.54 369.1L37.27 384z" />
      </svg>
    ),
  };

  return (
    <Stack size="full" className="mb-3">
      <button
        type="button"
        onClick={() => setActiveTripModalKind(kind)}
        className="flex w-full max-w-[750px] items-center gap-3 rounded-2xl border border-solid border-k-gray bg-white p-4 text-left transition-colors hover:border-navy-200"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-k-orange/10 fill-k-orange text-k-orange">
          {bookingTypeIcon[kind]}
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-sm font-semibold text-k-h">{title}</span>
          <span className="text-sm text-k-p">
            {t('description', { type: kind })}
          </span>
        </span>
        <i className="fa-regular fa-chevron-right shrink-0 text-gray-400" />
      </button>
      {booking && Boolean(activeTripModalKind) && (
        <TripModal
          title={title}
          kind={activeTripModalKind}
          booking={data?.data}
          onRequestClose={() => setActiveTripModalKind()}
        />
      )}
    </Stack>
  );
};

export default TripItem;
